// feat-130 — shared types + pure formatting helpers for the Shift-Interaction
// Evaluation dashboard. Kept dependency-free (no React / lucide / date-fns imports)
// so it is unit-testable under CRA's Jest without ESM transform headaches.
//
// feat-133 Batch-5: the SQL query builders that used to live here were removed —
// the stats/worklist/location reads now run server-side via the admin-gated,
// %s-parameterized /api/admin/interaction-evaluations/* endpoints (ADR 0005 DP-5a).
// Only the row/option types, the {1,7,14,30} window enum, and the num/pct/prettyType
// display helpers remain.

export const WINDOW_OPTIONS = [1, 7, 14, 30] as const;
export type WindowDays = (typeof WINDOW_OPTIONS)[number];
export const DEFAULT_WINDOW: WindowDays = 14;

export interface StatRow {
  interaction_type: string | null;
  total: number;
  verified: number;
  correct: number;
  incorrect: number;
  unverified: number;
}

// feat-156 — Interaction Judge validation. Rows from the SEPARATE admin-scoped
// endpoint `GET /api/admin/interaction-evaluations/judge-stats` (ADR 0001 §6 — a
// NEW sibling of /stats, leaving the existing endpoint untouched for the security
// gate). Returned per interaction_type for the offline judge's scope
// (initial_greeting + server_upsell). The frontend merges these onto the human
// `StatRow`s by `interaction_type` and never recomputes the percentages
// (single-source-of-truth, see feedback_metrics_single_source):
//   • discarded_pct — live double-check rejected / (confirmed + rejected), error
//     EXCLUDED; null when the denominator is zero (no live judgements yet).
//   • judge_agreement_pct — offline judge confirmed / (confirmed + rejected),
//     error EXCLUDED; null when zero denominator (not judged yet).
// Both are rounded numbers in [0,100]. The raw counts are exposed for honest
// rendering / QA but are ignored by the current UI. Everything is null until the
// `interaction_judgement` table is populated → the columns render '—' (expected).
export interface JudgeStatRow {
  interaction_type: string;
  discarded_pct: number | null;
  judge_agreement_pct: number | null;
  // Raw counts (optional; currently unused by the UI, kept for future honest render).
  live_confirmed?: number;
  live_rejected?: number;
  live_error?: number;
  offline_confirmed?: number;
  offline_rejected?: number;
  offline_error?: number;
  // feat-174 — `uncertain` count (windows the offline judge flagged as genuinely
  // insufficient). Surfaced as its own column; EXCLUDED from judge_agreement_pct
  // (uncertain is neither agreement nor disagreement — the denominator stays
  // confirmed + rejected, computed server-side).
  live_uncertain?: number;
  offline_uncertain?: number;
}

export interface WorklistRow {
  shift_id: string;
  server_id: string;
  server_name: string | null;
  location_id: string | null;
  location_name: string | null;
  shift_created: string;
  shift_ended: string | null;
  interaction_count: number;
}

export interface LocationOption {
  id: string;
  name: string;
}

// feat-191 Part E — per-grader throughput and agreement, grouped by
// shift_interaction.verified_by. ADMIN-ONLY: this is oversight OF evaluators and it
// names other graders by email, so the panel is absent for an evaluator and the
// endpoint 403s for their token (AC13).
export interface EvaluatorStatRow {
  /** = shift_interaction.verified_by, i.e. the grader's email. */
  evaluator: string;
  grades_total: number;
  grades_correct: number;
  grades_incorrect: number;
  /** UTC, no zone suffix — same convention as every other timestamp on this page. */
  last_verified_at: string | null;
  /**
   * Grades that carry a CURRENT decisive offline-judge verdict — NOT grades_total.
   * 42 grades of which 12 were judged is an agreement rate over 12. Rendering the
   * percentage without this number beside it is lying by omission, and it is the
   * single design requirement on this panel.
   */
  agreement_denominator: number;
  /** Sent so the ratio can be AUDITED. Never re-derive agreement_pct from it. */
  agreement_matches: number;
  /**
   * Integer 0–100, or NULL. Null means EITHER a zero denominator OR a denominator
   * below `min_agreement_denominator` — deliberate suppression, not missing data.
   * "100% agreement" over 2 judged items is the most misleading number this panel
   * could show about a contractor and the one most likely to be acted on.
   */
  agreement_pct: number | null;
}

export interface EvaluatorStatsResponse {
  days: number;
  location_id: string | null;
  /**
   * "verified_at" — when the GRADING happened. Every sibling endpoint on this page
   * windows on s.created, when the INTERACTION happened. So this panel and the stat
   * cards above it answer different questions under the same window label and will
   * not reconcile; that is by design. Rendered into the panel's own header rather
   * than assumed, so nobody files the difference as a bug.
   */
  window_basis: string;
  /** The suppression threshold, sent rather than hardcoded in two places. */
  min_agreement_denominator: number;
  count: number;
  evaluators: EvaluatorStatRow[];
}

// feat-133 Batch-5: the SQL builders (buildStatsSql / buildWorklistSql) and the
// client-side UUID location guard (locationClause / windowClause / UUID_RE) were
// REMOVED — the stats/worklist reads now run server-side via the admin-gated,
// %s-parameterized /api/admin/interaction-evaluations/* endpoints (days-enum +
// UUID validation are enforced by the backend). Only the shared types + pure
// formatting helpers remain.

// MySQL returns COUNT/SUM as strings; coerce defensively.
export const num = (v: unknown): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? 0));
  return Number.isFinite(n) ? n : 0;
};

export const pct = (part: number, whole: number): string =>
  whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';

// feat-156 — render a backend-computed percentage (0–100) verbatim, NOT a ratio.
// Unlike `pct`, this does NOT divide: the Discarded % / Judge agreement % values
// are computed once in the backend and only formatted here (feedback_metrics_
// single_source). Null / non-finite → '—' (no judgement rows yet, or type is
// outside the offline judge's scope).
export const judgePct = (value: number | null | undefined): string =>
  value != null && Number.isFinite(value) ? `${Math.round(value)}%` : '—';

// Cross-tenant view: render the raw interaction_type (title-cased), never a
// tenant-specific label map (per-tenant metric names would mislabel other tenants).
// Unknown / new types are shown verbatim, never dropped (ADR D4).
export const prettyType = (t: string | null): string =>
  t ? t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : 'Unknown';

// feat-159 — type-first review. Only these 3 interaction types are reviewable
// one-at-a-time (locked with Preston 2026-07-15). The other detected types stay
// non-interactive on the eval table ("review not available yet"). Keep this in
// lockstep with the backend's allow-list (else 422); the FE gates the drill-down
// so a disallowed type can't be requested.
export const REVIEW_TYPES = ['initial_greeting', 'server_upsell', 'drink_promotion'] as const;
export type ReviewType = (typeof REVIEW_TYPES)[number];

export const isReviewType = (t: string | null | undefined): t is ReviewType =>
  !!t && (REVIEW_TYPES as readonly string[]).includes(t);

// The offline judge (feat-156) covers greeting + upsell only. drink_promotion is
// human-review-only for now → its judge panel renders "not judged" (never '—',
// which would read as a data gap).
export const JUDGED_REVIEW_TYPES = ['initial_greeting', 'server_upsell'] as const;

/**
 * feat-199 — the types whose live-double-check REJECTIONS are reviewable.
 *
 * Lives here rather than in `scopedReadsService` on purpose: it is DATA, not I/O, and
 * every consumer test mocks that service with an explicit object — so importing a
 * constant from it silently yields `undefined` in those suites. Same reason feat-186
 * put the bucket union in this module.
 *
 * DELIBERATELY NOT the same set as {@link JUDGED_REVIEW_TYPES}, which mirrors the
 * OFFLINE judge's scoping. They coincide today and mean different things: `drink_promotion`
 * is excluded here because it is AUTO-CONFIRMED before any model call
 * (`transcript_annotator.py:330-331`), so its verdict is always `confirmed` and a
 * REJECTION is unreachable — not because it is unjudged. Collapsing the two would be
 * right for the wrong reason, which is the version that rots.
 */
export const REJECTION_REVIEW_TYPES = ['initial_greeting', 'server_upsell'] as const;
export type RejectionReviewType = (typeof REJECTION_REVIEW_TYPES)[number];

export const isRejectionReviewType = (t: string | null | undefined): t is RejectionReviewType =>
  !!t && (REJECTION_REVIEW_TYPES as readonly string[]).includes(t);

export const isJudgedType = (t: string | null | undefined): boolean =>
  !!t && (JUDGED_REVIEW_TYPES as readonly string[]).includes(t);

// feat-160 (ADR 0001) — map a server-classified disagreement token to its
// reviewer-facing reason-chip label. Rendered VERBATIM; the FE never recomputes
// whether a pair fired (classification is server-authoritative,
// feedback_metrics_single_source). The `human_vs_offline` wording branches on the
// human's own verdict — presentation only; the token itself is server-decided.
// An unknown token renders raw (never dropped), mirroring prettyType's policy.
export const disagreementChipLabel = (
  token: string,
  correctHumanVerified: boolean | null | undefined,
): string => {
  switch (token) {
    case 'annotator_vs_offline':
      return 'Judge rejected';
    case 'annotator_vs_live':
      return 'Live double-check rejected';
    // feat-174 — NOT a conflict; the judge was uncertain and the interaction is
    // routed to human review. Distinct wording + chip styling from the rejects.
    case 'offline_uncertain':
      return 'Judge uncertain — needs review';
    case 'human_vs_offline':
      return correctHumanVerified === true
        ? 'You said Correct, judge rejected'
        : 'You said Incorrect, judge confirmed';
    default:
      return token;
  }
};

// ============================================================================
// feat-186 — reviewer-controlled queue composition. The reviewer dials the share
// of each review batch drawn from three SERVER-CLASSIFIED buckets. Bucket
// membership is authoritative on the backend (`item.bucket`, ADR 0001 D-B) — the
// FE renders/labels/counts from that field and NEVER re-derives it from the
// disagreement tokens (feedback_metrics_single_source).
// ============================================================================

// The three buckets, exactly as the backend tags each item (ADR 0001 D-B).
export type ReviewBucket = 'discrepancy' | 'uncertain' | 'agreement';
// Priority order — mirrors the backend partition + the D8 graceful-degrade fill
// order (Discrepancy → Uncertain → Agreement).
export const REVIEW_BUCKETS: readonly ReviewBucket[] = ['discrepancy', 'uncertain', 'agreement'];

export const isReviewBucket = (v: string | null | undefined): v is ReviewBucket =>
  v === 'discrepancy' || v === 'uncertain' || v === 'agreement';

/** Three integer shares of the batch (they sum to 100 by construction). */
export interface BucketShares {
  discrepancy: number;
  uncertain: number;
  agreement: number;
}

// Bar-segment / legend-dot fill hues — REUSE the established review-surface
// semantics, NO new design token (feat-186 charter + mockup §notes):
//   Discrepancy = the ⚠ disagreement-chip warning family (warning.solid);
//   Uncertain   = the feat-174 raw-indigo "uncertain" island (indigo-500);
//   Agreement   = the "confirmed" success green.
// STATIC literal classes on purpose — a dynamic `bg-${bucket}` would be silently
// purged by Tailwind's content scan (see memory tailwind-purges-dynamic-classnames),
// rendering the bar unstyled. Keyed lookup of whole class strings is purge-safe.
export const BUCKET_SEGMENT_CLASS: Record<ReviewBucket, string> = {
  discrepancy: 'bg-warning-solid',
  uncertain: 'bg-indigo-500',
  agreement: 'bg-success',
};

export const BUCKET_LABEL: Record<ReviewBucket, string> = {
  discrepancy: 'Discrepancy',
  uncertain: 'Uncertain',
  agreement: 'Agreement',
};

export interface CompositionPreset {
  id: string;
  label: string;
  shares: BucketShares;
}

// R1 / D2 — Standard 70/25/5 is the FIRM default and the first-load state.
// Discrepancies 100/0/0 reproduces the retired "Only disagreements" filter
// (AC2 / AC6); Even 34/33/33 is the audit mix. The latter two are design-lead
// proposals (the mockup's approved preset row); only the 70/25/5 default is firm.
export const COMPOSITION_PRESETS: readonly CompositionPreset[] = [
  { id: 'standard', label: 'Standard', shares: { discrepancy: 70, uncertain: 25, agreement: 5 } },
  { id: 'discrepancies', label: 'Discrepancies', shares: { discrepancy: 100, uncertain: 0, agreement: 0 } },
  { id: 'even', label: 'Even', shares: { discrepancy: 34, uncertain: 33, agreement: 33 } },
];

/** The firm default mix (70/25/5) — first load and the "Standard" preset. */
export const DEFAULT_SHARES: BucketShares = COMPOSITION_PRESETS[0].shares;

/**
 * Normalize three raw shares to three integers summing to EXACTLY 100, via
 * largest-remainder rounding (a naive per-bucket round can total 99 or 101 and
 * misrender the bar width). Tie-break on equal fractional parts is D→U→A, which
 * mirrors the backend's own largest-remainder tie-break (ADR 0001 D-C step 1) so
 * the FE requested-bar agrees with the server's per-page targets. All-zero input
 * (e.g. a malformed URL) falls back to the default rather than a blank/NaN bar.
 */
export const normalizeShares = (s: BucketShares): BucketShares => {
  const total = s.discrepancy + s.uncertain + s.agreement;
  if (!(total > 0)) return { ...DEFAULT_SHARES };
  const scale = 100 / total;
  const scaled: Record<ReviewBucket, number> = {
    discrepancy: s.discrepancy * scale,
    uncertain: s.uncertain * scale,
    agreement: s.agreement * scale,
  };
  const out: Record<ReviewBucket, number> = {
    discrepancy: Math.floor(scaled.discrepancy),
    uncertain: Math.floor(scaled.uncertain),
    agreement: Math.floor(scaled.agreement),
  };
  let remainder = 100 - (out.discrepancy + out.uncertain + out.agreement);
  // Hand the leftover units to the largest fractional parts. REVIEW_BUCKETS is in
  // D→U→A order and Array.prototype.sort is stable (V8 / Node), so equal remainders
  // keep that priority order — matching the backend tie-break.
  const byRemainder = [...REVIEW_BUCKETS].sort(
    (a, b) => scaled[b] - out[b] - (scaled[a] - out[a]),
  );
  for (let i = 0; i < byRemainder.length && remainder > 0; i += 1, remainder -= 1) {
    out[byRemainder[i]] += 1;
  }
  return out;
};

/** The preset whose shares match exactly, or null ("Custom") for a free tune. */
export const matchPreset = (s: BucketShares): CompositionPreset | null =>
  COMPOSITION_PRESETS.find(
    (p) =>
      p.shares.discrepancy === s.discrepancy &&
      p.shares.uncertain === s.uncertain &&
      p.shares.agreement === s.agreement,
  ) ?? null;

/**
 * Agreement max-days-back option buttons (D3/R2). The mockup's set is
 * {7,14,30,90}; we always fold in the current review Window so the default
 * (= the Window, ADR 0001 D-E) is always a selectable button — e.g. Window 1d
 * yields {1,7,14,30,90}. Sorted, de-duped. The backend accepts any positive int
 * (D-I), so these are presentational presets, not an enforced enum.
 */
export const agreementMaxDaysOptions = (windowDays: number): number[] =>
  Array.from(new Set<number>([windowDays, 7, 14, 30, 90])).sort((a, b) => a - b);

/**
 * A random 32-bit unsigned int as a decimal string — the agreement sample seed
 * (D7). Generated once on first load and written into the URL so a shared link
 * reproduces the EXACT agreement spot-check (ADR 0001 D-D). Opaque to the backend
 * (used only inside the seeded hash).
 */
export const generateAgreementSeed = (): string =>
  String(Math.floor(Math.random() * 0x100000000));

/** Parse a `shares` URL param of the form "d-u-a" (e.g. "70-25-5"). */
export const parseSharesParam = (raw: string | null): BucketShares | null => {
  if (!raw) return null;
  const parts = raw.split('-');
  if (parts.length !== 3) return null;
  const nums = parts.map((p) => Number(p));
  if (nums.some((n) => !Number.isFinite(n) || n < 0)) return null;
  const [discrepancy, uncertain, agreement] = nums;
  if (discrepancy + uncertain + agreement <= 0) return null;
  return { discrepancy, uncertain, agreement };
};

/** Serialize shares for the URL as "d-u-a" (normalized so the link is canonical). */
export const sharesToParam = (s: BucketShares): string => {
  const n = normalizeShares(s);
  return `${n.discrepancy}-${n.uncertain}-${n.agreement}`;
};

// Render seconds-from-shift-start as m:ss (a clock reads better than the raw
// `123.4s` used in the dense transcript view). Negative / non-finite guards to
// '0:00' — these are offsets, never wall-clock.
export const formatClock = (seconds: unknown): string => {
  const s = num(seconds);
  const whole = Math.max(0, Math.floor(s));
  const m = Math.floor(whole / 60);
  const sec = whole % 60;
  return `${m}:${sec.toString().padStart(2, '0')}`;
};
